import React from 'react';
import { Loader, Message, Icon, Label } from 'semantic-ui-react';
import { TrackingInfoDto } from '../../types/tracking';

export interface TrackingTimelineProps {
  trackingCode: string;
  loading: boolean;
  info: TrackingInfoDto | null;
  /** Mensagem de aviso/erro quando não há dados úteis além do loading */
  emptyMessage?: string | null;
}

function formatPtBr(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/**
 * Linha do tempo de eventos dos Correios — responsiva (viewport estreita: scroll interno do modal).
 */
export const TrackingTimeline: React.FC<TrackingTimelineProps> = ({
  trackingCode,
  loading,
  info,
  emptyMessage,
}) => {
  if (loading) {
    return (
      <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader active inline="centered" content="Consultando Correios..." />
      </div>
    );
  }

  if (emptyMessage) {
    return <Message warning>{emptyMessage}</Message>;
  }

  if (!info) {
    return null;
  }

  const eventos = info.eventos ?? [];

  if (eventos.length === 0) {
    return (
      <Message info>
        Nenhum evento de rastreamento disponível para o código{' '}
        <strong style={{ wordBreak: 'break-all' }}>{info.codigo || trackingCode}</strong> neste momento.
      </Message>
    );
  }

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden', paddingBottom: 8 }}>
      <div style={{ marginBottom: '1rem', wordBreak: 'break-word' }}>
        <strong>Código:</strong>{' '}
        <span style={{ wordBreak: 'break-all' }}>{info.codigo || trackingCode}</span>
      </div>
      {(info.statusAtual || info.entregue) && (
        <Message success={info.entregue} info={!info.entregue} size="small" style={{ marginBottom: '1rem' }}>
          {info.statusAtual && (
            <>
              <strong>Status atual:</strong> {info.statusAtual}
            </>
          )}
          {info.entregue ? ' · Objeto entregue' : ''}
        </Message>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {eventos.map((ev, idx) => (
          <div
            key={`${ev.dataHora}-${idx}`}
            style={{
              borderLeft: '3px solid #2185d0',
              paddingLeft: 12,
              paddingBottom: 8,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
                marginBottom: 4,
                minWidth: 0,
              }}
            >
              <Icon name="clock outline" />
              <span style={{ fontWeight: 600 }}>{formatPtBr(ev.dataHora)}</span>
              {ev.codigoCorreios && (
                <Label size="tiny" color="blue">
                  {ev.codigoCorreios}
                </Label>
              )}
            </div>
            {ev.descricao && (
              <div style={{ marginBottom: 4, wordBreak: 'break-word' }}>{ev.descricao}</div>
            )}
            {(ev.local || ev.unidadeOrigem) && (
              <div style={{ fontSize: '0.9em', color: '#666', wordBreak: 'break-word' }}>
                <Icon name="map marker alternate" />
                {ev.local || ev.unidadeOrigem}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
